/* eslint-disable react/require-default-props */
/* eslint-disable react/destructuring-assignment */
/**
 * 複数選択可能なドロップダウンのコンポーネントと、そのコンポーネントが内部で利用するコンポーネント、関数をまとめたファイルです。
 * 参考：
 * https://react-select.com/home
 * https://stackoverflow.com/questions/62795886/returning-correct-value-using-react-select-and-react-hook-form/67427010#67427010
 */
import { useMemo } from 'react';
import { Control, Controller, FieldValues, Path, UnPackAsyncDefaultValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Select, { OptionProps } from 'react-select';

/**
 * 選択肢のインターフェース
 */
interface OptionType {
  value: string | number | undefined;
  label: string | undefined;
}

/**
 * 選択肢のコンポーネントをスタイル付けしてカスタマイズしたコンポーネント
 * 参考：
 * https://react-select.com/components
 * @param props
 * @returns
 */
function Option(props: OptionProps<OptionType>) {
  return (
    <div style={{ paddingLeft: '8px', paddingRight: '8px' }} className="multi-select">
      <input
        type="checkbox"
        id={props.data.value?.toString()}
        className="multiple-select-checkbox"
        checked={props.isSelected}
        onChange={() => {
          props.selectOption(props.data);
        }}
      />{' '}
      <label htmlFor={props.data.value?.toString()}>{props.label}</label>
    </div>
  );
}
interface MainProps<T extends FieldValues> {
  id?: string | undefined;
  name: Path<UnPackAsyncDefaultValues<T>>;
  options: OptionType[];
  control: Control<T>;
  isDisabled: boolean;
}

/**
 * 複数選択可能なドロップダウンの中心部分
 * 参考：
 * https://stackoverflow.com/questions/62795886/returning-correct-value-using-react-select-and-react-hook-form/67427010#67427010
 * @param
 * @returns
 */
export function MultiSelectMain<T extends FieldValues>({ id, name, options, control, isDisabled }: MainProps<T>) {
  const { t } = useTranslation();

  return (
    <div className="multi-select">
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <Select
            id={id}
            placeholder={t('選択してください')}
            components={{ Option }}
            options={options}
            value={options.filter(
              (x) =>
                // eslint-disable-next-line @typescript-eslint/no-unsafe-call
                field.value != null && field.value.includes(x.value)
            )}
            onChange={(newValue) => {
              field.onChange(newValue.map((x) => x.value));
            }}
            isMulti
            // menuPortalTarget は、メニューが他の要素の後ろに隠れないようにするため、設定したほうが良さそうです。
            // https://github.com/JedWatson/react-select/issues/3263
            menuPortalTarget={document.querySelector('body')}
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            isDisabled={isDisabled}
            styles={{
              // モックに合わせて、スタイルを設定します。
              control: (baseStyles) => ({
                ...baseStyles,
                width: '100%',
                padding: '0',
                borderRadius: '50px',
                border: 'none',
                boxSizing: 'border-box',
                backgroundColor: '#f4f4f4',
                fontSize: '12px',
                margin: '0',
                minHeight: '25px',
              }),
              placeholder: (baseStyles) => ({
                ...baseStyles,
                paddingLeft: '8px',
              }),
              valueContainer: (baseStyles) => ({
                ...baseStyles,
                padding: '0px 0px 0px 8px',
              }),
              dropdownIndicator: (baseStyles) => ({
                ...baseStyles,
                padding: '0px 8px 0px 0px',
              }),
              clearIndicator: (baseStyles) => ({
                ...baseStyles,
                padding: '0px 8px',
              }),
              // menu が他の要素の後ろに隠れないようにするため、zIndex: 9999 を指定します。
              menuPortal: (baseStyles) => ({ ...baseStyles, zIndex: 10100 }),
              input: (baseStyles) => ({ ...baseStyles, padding: '0px', margin: '0px' }),
            }}
          />
        )}
      />
    </div>
  );
}

interface Props<S, T extends FieldValues> {
  id?: string | undefined;
  name: Path<UnPackAsyncDefaultValues<T>>;
  options: S[];
  control: Control<T>;
  isDisabled: boolean;
  getValue: (x: S) => number | string | undefined;
  getLabel: (x: S) => string | undefined;
}

/**
 * 複数選択可能なドロップダウンのコンポーネント
 * 基本部分は MultiSelectMain で実装し、こちらでは選択肢のみを生成します。
 * @param
 * @returns
 */
function MultiSelect<S, T extends FieldValues>({
  id,
  name,
  options,
  control,
  isDisabled,
  getValue,
  getLabel,
}: Props<S, T>) {
  const valueLabelOptions = useMemo(
    () => options.map((x) => ({ value: getValue(x), label: getLabel(x) })),
    [options, getValue, getLabel]
  );

  return (
    <span className="dropdown">
      <MultiSelectMain id={id} name={name} options={valueLabelOptions} control={control} isDisabled={isDisabled} />
    </span>
  );
}

MultiSelect.defaultProps = {
  id: undefined,
};

export default MultiSelect;

